import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';
import { ProjectInfo } from '../../types';

interface BackupRecord {
    id: string;
    collection_name: string;
    doc_id: string;
    payload: any;
    changed_at: string;
}

interface BackupPackage {
    packageId: string; // YYYY-MM-DD HH:00
    timestamp: string; // Latest changed_at
    records: BackupRecord[];
    expanded?: boolean;
}

interface BackupsManagerProps {
    projects?: (ProjectInfo & { id: string })[];
}

const getHumanReadableDocId = (docId: string): string => {
    switch (docId) {
        case 'tasks': return 'Planificación Semanal (Carta Gantt y Tareas)';
        case 'levels': return 'Niveles de la Obra (EDT)';
        case 'units': return 'Unidades Físicas de la Obra (EDT)';
        case 'matrixData': return 'Matriz de Avance (Planificación, Calidad y Pagos)';
        case 'notifications': return 'Notificaciones del Sistema';
        case 'protocolConfigs': return 'Configuración de Fichas/Protocolos de Calidad';
        case 'savedProtocols': return 'Firmas y Estados de Protocolos de Calidad';
        case 'weeklyPlans': return 'Planes Semanales e Historial';
        case 'lookaheadPlans': return 'Planes Lookahead (Plan de Restricciones)';
        case 'subcontractors': return 'Directorio de Subcontratistas';
        case 'contracts': return 'Contratos y Convenios de Subcontratos';
        case 'paymentStatements': return 'Estados de Pago (EP) Generados';
        case 'budgetProjects': return 'Proyectos de Presupuesto';
        case 'budgetResources': return 'Recursos de Presupuesto';
        case 'budgetItems': return 'Partidas de Presupuesto';
        case 'apuData': return 'Análisis de Precios Unitarios (APU)';
        case 'budgetTree': return 'Árbol de Estructura de Presupuesto';
        case 'subcontractTypes': return 'Tipos de Subcontratos Maestro';
        case 'cncGroups': return 'Maestro de Causas de No Cumplimiento';
        case 'constraintGroups': return 'Maestro de Grupos de Restricciones';
        case 'baselines': return 'Líneas Base del Proyecto';
        case 'progressHistory': return 'Historial de Avance del Proyecto';
        case 'activities': return 'Historial de Actividades Recientes';
        case 'projects': return 'Listado General de Obras';
        case 'companyConfig': return 'Configuración de Marca/Empresa';
        case 'globalUsers': return 'Directorio Global de Usuarios';
        case 'globalProfiles': return 'Perfiles y Permisos Globales';
        default: 
            if (docId.startsWith('project_')) return `Información del Proyecto (${docId})`;
            return docId;
    }
};

const getHumanReadableCollectionOuter = (colName: string): string => {
    if (colName === 'data') return 'Parámetros Globales de la Empresa';
    if (colName.startsWith('project_')) {
        const projId = colName.split('_')[1];
        return `Obra / Proyecto #${projId}`;
    }
    return colName;
};

const getPreviousRecord = (currentRec: BackupRecord, allRecs: BackupRecord[]): BackupRecord | null => {
    const currentTime = new Date(currentRec.changed_at).getTime();
    let bestPrev: BackupRecord | null = null;
    let bestPrevTime = 0;
    
    for (const r of allRecs) {
        if (r.id === currentRec.id) continue;
        if (r.collection_name === currentRec.collection_name && r.doc_id === currentRec.doc_id) {
            const rTime = new Date(r.changed_at).getTime();
            if (rTime < currentTime && rTime > bestPrevTime) {
                bestPrevTime = rTime;
                bestPrev = r;
            }
        }
    }
    return bestPrev;
};

interface BackupDelta {
    added: string[];
    deleted: string[];
    modified: string[];
}

const computeDelta = (rec: BackupRecord, prevRec: BackupRecord | null): BackupDelta | null => {
    if (!rec.payload) return null;
    
    const currentList = Array.isArray(rec.payload) 
        ? rec.payload 
        : (rec.payload.list && Array.isArray(rec.payload.list) ? rec.payload.list : null);
        
    const prevList = prevRec && prevRec.payload
        ? (Array.isArray(prevRec.payload) ? prevRec.payload : (prevRec.payload.list && Array.isArray(prevRec.payload.list) ? prevRec.payload.list : null))
        : null;

    const formatDateShort = (dStr: string) => {
        if (!dStr) return '';
        const d = new Date(dStr);
        if (isNaN(d.getTime())) return dStr;
        return d.toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    switch (rec.doc_id) {
        case 'weeklyPlans': {
            if (!currentList) return null;
            if (!prevList) {
                return {
                    added: currentList.map((p: any) => `Planificación Semanal #${p.id} (${formatDateShort(p.startDate)} al ${formatDateShort(p.endDate)}) creada con estado "${p.status}" y ${p.allocations?.length || 0} asignaciones.`),
                    deleted: [],
                    modified: []
                };
            }
            const added: string[] = [];
            const deleted: string[] = [];
            const modified: string[] = [];

            currentList.forEach((plan: any) => {
                const prevPlan = prevList.find((p: any) => p.id === plan.id);
                if (!prevPlan) {
                    added.push(`Planificación Semanal #${plan.id} (${formatDateShort(plan.startDate)} al ${formatDateShort(plan.endDate)}) creada con estado "${plan.status}" y ${plan.allocations?.length || 0} asignaciones.`);
                } else {
                    const changes: string[] = [];
                    if (prevPlan.status !== plan.status) {
                        changes.push(`Estado cambió de "${prevPlan.status}" a "${plan.status}"`);
                    }
                    if ((prevPlan.allocations?.length || 0) !== (plan.allocations?.length || 0)) {
                        changes.push(`Asignaciones en terreno cambiaron de ${prevPlan.allocations?.length || 0} a ${plan.allocations?.length || 0} tareas`);
                    }
                    if (prevPlan.startDate !== plan.startDate || prevPlan.endDate !== plan.endDate) {
                        changes.push(`Fechas cambiaron de (${formatDateShort(prevPlan.startDate)} - ${formatDateShort(prevPlan.endDate)}) a (${formatDateShort(plan.startDate)} - ${formatDateShort(plan.endDate)})`);
                    }
                    if (changes.length > 0) {
                        modified.push(`Planificación Semanal #${plan.id}: ${changes.join(', ')}.`);
                    }
                }
            });

            prevList.forEach((prevPlan: any) => {
                const currentPlan = currentList.find((p: any) => p.id === prevPlan.id);
                if (!currentPlan) {
                    deleted.push(`Planificación Semanal #${prevPlan.id} (${formatDateShort(prevPlan.startDate)} al ${formatDateShort(prevPlan.endDate)}) eliminada.`);
                }
            });

            return { added, deleted, modified };
        }
        case 'lookaheadPlans': {
            if (!currentList) return null;
            if (!prevList) {
                return {
                    added: currentList.map((p: any) => `Planificación Intermedia (Lookahead) de ${p.weeksAhead || 4} semanas (${formatDateShort(p.startDate)} al ${formatDateShort(p.endDate)}) creada con ${p.constraints?.length || 0} restricciones.`),
                    deleted: [],
                    modified: []
                };
            }
            const added: string[] = [];
            const deleted: string[] = [];
            const modified: string[] = [];

            currentList.forEach((plan: any) => {
                const prevPlan = prevList.find((p: any) => p.id === plan.id);
                if (!prevPlan) {
                    added.push(`Planificación Intermedia (Lookahead) de ${plan.weeksAhead || 4} semanas (${formatDateShort(plan.startDate)} al ${formatDateShort(plan.endDate)}) agregada.`);
                } else {
                    const changes: string[] = [];
                    if (prevPlan.status !== plan.status) {
                        changes.push(`Estado cambió de "${prevPlan.status}" a "${plan.status}"`);
                    }
                    if ((prevPlan.constraints?.length || 0) !== (plan.constraints?.length || 0)) {
                        changes.push(`Cantidad de restricciones cambió de ${prevPlan.constraints?.length || 0} a ${plan.constraints?.length || 0}`);
                    }
                    if (changes.length > 0) {
                        modified.push(`Lookahead (${formatDateShort(plan.startDate)} - ${formatDateShort(plan.endDate)}): ${changes.join(', ')}.`);
                    }
                }
            });

            prevList.forEach((prevPlan: any) => {
                const currentPlan = currentList.find((p: any) => p.id === prevPlan.id);
                if (!currentPlan) {
                    deleted.push(`Planificación Intermedia (${formatDateShort(prevPlan.startDate)} al ${formatDateShort(prevPlan.endDate)}) eliminada.`);
                }
            });

            return { added, deleted, modified };
        }
        case 'contracts': {
            if (!currentList) return null;
            if (!prevList) {
                return {
                    added: currentList.map((c: any) => `Contrato de Subcontrato "${c.id}" creado por $${(c.totalAmount || 0).toLocaleString('es-CL')} CLP con estado "${c.status}".`),
                    deleted: [],
                    modified: []
                };
            }
            const added: string[] = [];
            const deleted: string[] = [];
            const modified: string[] = [];

            currentList.forEach((contract: any) => {
                const prevContract = prevList.find((c: any) => c.id === contract.id);
                if (!prevContract) {
                    added.push(`Contrato "${contract.id}" registrado por un total de $${(contract.totalAmount || 0).toLocaleString('es-CL')} CLP.`);
                } else {
                    const changes: string[] = [];
                    if (prevContract.status !== contract.status) {
                        changes.push(`Estado cambió de "${prevContract.status}" a "${contract.status}"`);
                    }
                    if (prevContract.totalAmount !== contract.totalAmount) {
                        changes.push(`Monto total cambió de $${(prevContract.totalAmount || 0).toLocaleString('es-CL')} a $${(contract.totalAmount || 0).toLocaleString('es-CL')}`);
                    }
                    if (changes.length > 0) {
                        modified.push(`Contrato "${contract.id}": ${changes.join(', ')}.`);
                    }
                }
            });

            prevList.forEach((prevContract: any) => {
                const currentContract = currentList.find((c: any) => c.id === prevContract.id);
                if (!currentContract) {
                    deleted.push(`Contrato "${prevContract.id}" eliminado.`);
                }
            });

            return { added, deleted, modified };
        }
        case 'paymentStatements': {
            if (!currentList) return null;
            if (!prevList) {
                return {
                    added: currentList.map((ep: any) => `Estado de Pago "${ep.id}" registrado por un neto de $${(ep.netAmount || ep.montoNeto || 0).toLocaleString('es-CL')} CLP (Estado: "${ep.status}").`),
                    deleted: [],
                    modified: []
                };
            }
            const added: string[] = [];
            const deleted: string[] = [];
            const modified: string[] = [];

            currentList.forEach((ep: any) => {
                const prevEp = prevList.find((e: any) => e.id === ep.id);
                if (!prevEp) {
                    added.push(`Estado de Pago "${ep.id}" registrado por $${(ep.netAmount || ep.montoNeto || 0).toLocaleString('es-CL')} CLP.`);
                } else {
                    const changes: string[] = [];
                    if (prevEp.status !== ep.status) {
                        changes.push(`Estado cambió de "${prevEp.status}" a "${ep.status}"`);
                    }
                    if ((prevEp.netAmount || prevEp.montoNeto || 0) !== (ep.netAmount || ep.montoNeto || 0)) {
                        changes.push(`Monto neto cambió de $${(prevEp.netAmount || prevEp.montoNeto || 0).toLocaleString('es-CL')} a $${(ep.netAmount || ep.montoNeto || 0).toLocaleString('es-CL')}`);
                    }
                    if (changes.length > 0) {
                        modified.push(`Estado de Pago "${ep.id}": ${changes.join(', ')}.`);
                    }
                }
            });

            prevList.forEach((prevEp: any) => {
                const currentEp = currentList.find((e: any) => e.id === prevEp.id);
                if (!currentEp) {
                    deleted.push(`Estado de Pago "${prevEp.id}" eliminado.`);
                }
            });

            return { added, deleted, modified };
        }
        case 'tasks': {
            if (!currentList) return null;
            
            const flatten = (list: any[]): any[] => {
                let res: any[] = [];
                list.forEach(t => {
                    res.push(t);
                    if (t.children && Array.isArray(t.children)) {
                        res = res.concat(flatten(t.children));
                    }
                });
                return res;
            };

            const currentFlat = flatten(currentList);
            const prevFlat = prevList ? flatten(prevList) : null;

            if (!prevFlat) {
                return {
                    added: [`Carta Gantt inicializada con ${currentFlat.filter(t => t.type !== 'folder').length} tareas programadas.`],
                    deleted: [],
                    modified: []
                };
            }

            const added: string[] = [];
            const deleted: string[] = [];
            const modified: string[] = [];

            currentFlat.forEach((task: any) => {
                const prevTask = prevFlat.find((t: any) => t.id === task.id);
                if (!prevTask) {
                    if (task.type !== 'folder') {
                        added.push(`Tarea "${task.name}" programada (Inicio: ${formatDateShort(task.start)}, Duración: ${task.duration} días).`);
                    }
                } else {
                    const changes: string[] = [];
                    if (prevTask.progress !== task.progress) {
                        changes.push(`Avance cambió de ${prevTask.progress || 0}% a ${task.progress || 0}%`);
                    }
                    if (prevTask.start !== task.start || prevTask.end !== task.end) {
                        changes.push(`Fechas cambiaron de (${formatDateShort(prevTask.start)} - ${formatDateShort(prevTask.end)}) a (${formatDateShort(task.start)} - ${formatDateShort(task.end)})`);
                    }
                    if (changes.length > 0 && task.type !== 'folder') {
                        modified.push(`Tarea "${task.name}": ${changes.join(', ')}.`);
                    }
                }
            });

            prevFlat.forEach((prevTask: any) => {
                const currentTask = currentFlat.find((t: any) => t.id === prevTask.id);
                if (!currentTask) {
                    if (prevTask.type !== 'folder') {
                        deleted.push(`Tarea "${prevTask.name}" eliminada.`);
                    }
                }
            });

            return { added, deleted, modified };
        }
        case 'activities': {
            if (!currentList) return null;
            if (!prevList) {
                return {
                    added: currentList.map((a: any) => `👤 ${a.user || a.usuario || 'Sistema'}: ${a.description || a.action}`),
                    deleted: [],
                    modified: []
                };
            }
            const added: string[] = [];
            currentList.forEach((act: any) => {
                const exists = prevList.some((pa: any) => pa.id === act.id || (pa.timestamp === act.timestamp && pa.description === act.description));
                if (!exists) {
                    added.push(`👤 ${act.user || act.usuario || 'Sistema'}: ${act.description || act.action}`);
                }
            });

            return { added, deleted: [], modified: [] };
        }
        default:
            return null;
    }
};

const BackupsManager: React.FC<BackupsManagerProps> = ({ projects }) => {
    const [backups, setBackups] = useState<BackupRecord[]>([]);
    const [packages, setPackages] = useState<BackupPackage[]>([]);
    const [currentRows, setCurrentRows] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isRestoring, setIsRestoring] = useState<string | null>(null);
    const [filterDocId, setFilterDocId] = useState<string>('');
    const [expandedPackages, setExpandedPackages] = useState<Record<string, boolean>>({});
    const [showTriggerPanel, setShowTriggerPanel] = useState(false);
    
    // Control variables for dynamic PostgreSQL trigger toggle
    const [backupsEnabled, setBackupsEnabled] = useState<boolean | null>(null);
    const [isUpdatingStatus, setIsUpdatingStatus] = useState<boolean>(false);
    const [rpcSupported, setRpcSupported] = useState<boolean>(false);
    const [activeTriggerTab, setActiveTriggerTab] = useState<'manual' | 'rpc'>('manual');

    // Custom Interactive Confirmation Modal State
    const [confirmModal, setConfirmModal] = useState<{
        isOpen: boolean;
        record: BackupRecord | null;
        isPackage: boolean;
        packageData: BackupPackage | null;
        onConfirm: () => void;
    }>({
        isOpen: false,
        record: null,
        isPackage: false,
        packageData: null,
        onConfirm: () => {}
    });

    // Tracks which backup records are fully expanded in their UI representation
    const [expandedRecords, setExpandedRecords] = useState<Record<string, boolean>>({});

    const getHumanReadableCollection = (colName: string): string => {
        if (colName === 'data') return 'Parámetros Globales de la Empresa';
        if (colName.startsWith('project_')) {
            const projId = colName.split('_')[1];
            const proj = projects?.find(p => p.id === projId);
            return proj ? proj.name : `Proyecto #${projId}`;
        }
        return colName;
    };

    const getNewStatePayload = (rec: BackupRecord): any => {
        // Find the next newer record for the same collection_name and doc_id
        const currentTime = new Date(rec.changed_at).getTime();
        let nextRec: BackupRecord | null = null;
        let nextRecTime = Infinity;

        for (const r of backups) {
            if (r.id === rec.id) continue;
            if (r.collection_name === rec.collection_name && r.doc_id === rec.doc_id) {
                const rTime = new Date(r.changed_at).getTime();
                if (rTime > currentTime && rTime < nextRecTime) {
                    nextRecTime = rTime;
                    nextRec = r;
                }
            }
        }

        if (nextRec) {
            return nextRec.payload;
        }

        // If there is no newer history record, the new state is the current state in app_data
        const currentMatch = currentRows.find(
            r => r.collection_name === rec.collection_name && r.doc_id === rec.doc_id
        );
        if (currentMatch) {
            return currentMatch.payload;
        }

        // Fallback if not found (e.g. completely deleted row)
        return Array.isArray(rec.payload) ? [] : (rec.payload && rec.payload.list ? { ...rec.payload, list: [] } : null);
    };

    const renderPayloadDetails = (rec: BackupRecord) => {
        if (!rec.payload) return <span className="text-slate-400">Sin datos de contenido</span>;

        try {
            const payload = rec.payload;

            // Date formatter utility
            const formatDateShort = (dStr: string) => {
                if (!dStr) return '';
                const d = new Date(dStr);
                if (isNaN(d.getTime())) return dStr;
                return d.toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' });
            };

            const dataList = Array.isArray(payload) ? payload : (payload.list && Array.isArray(payload.list) ? payload.list : null);
            const isRecordExpanded = !!expandedRecords[rec.id];

            // Calculate and display dynamic delta compared to the next state (correct Copy-on-Write transition)
            const newStatePayload = getNewStatePayload(rec);
            const newStateRec = { doc_id: rec.doc_id, payload: newStatePayload } as BackupRecord;
            const delta = computeDelta(newStateRec, rec);

            if (delta && (delta.added.length > 0 || delta.deleted.length > 0 || delta.modified.length > 0)) {
                return (
                    <div className="space-y-2.5 mt-1 text-xs">
                        <div 
                            onClick={() => setExpandedRecords(prev => ({ ...prev, [rec.id]: !prev[rec.id] }))}
                            className="font-bold text-slate-700 dark:text-slate-200 flex items-center gap-1.5 cursor-pointer hover:text-indigo-650 dark:hover:text-indigo-400 select-none active:scale-[0.99] transition-all"
                        >
                            <span className="material-symbols-outlined text-[16px] text-indigo-500">analytics</span>
                            <span>Detalle de Modificaciones en este Cambio:</span>
                            <span className="text-[9px] text-indigo-500 bg-indigo-50 dark:bg-indigo-955/35 px-1.5 py-0.5 rounded font-mono">
                                {isRecordExpanded ? 'contraer' : 'ver detalles'}
                            </span>
                        </div>
                        
                        <div className="pl-4 border-l-2 border-slate-200 dark:border-slate-800 space-y-2">
                            {/* Consolidated Badges */}
                            <div className="flex flex-wrap gap-2 text-[10px] font-extrabold uppercase tracking-wide">
                                {delta.added.length > 0 && (
                                    <span className="px-2 py-0.5 rounded bg-emerald-50 dark:bg-emerald-955/20 text-emerald-600 border border-emerald-100 dark:border-emerald-900/30">
                                        ➕ {delta.added.length} Agregado{delta.added.length === 1 ? '' : 's'}
                                    </span>
                                )}
                                {delta.modified.length > 0 && (
                                    <span className="px-2 py-0.5 rounded bg-amber-50 dark:bg-amber-955/20 text-amber-600 border border-amber-100 dark:border-amber-900/30">
                                        🔄 {delta.modified.length} Modificado{delta.modified.length === 1 ? '' : 's'}
                                    </span>
                                )}
                                {delta.deleted.length > 0 && (
                                    <span className="px-2 py-0.5 rounded bg-red-50 dark:bg-red-955/20 text-red-600 border border-red-100 dark:border-red-900/30">
                                        ❌ {delta.deleted.length} Eliminado{delta.deleted.length === 1 ? '' : 's'}
                                    </span>
                                )}
                            </div>
                            
                            {isRecordExpanded ? (
                                <div className="space-y-2 text-slate-600 dark:text-slate-355 font-medium leading-relaxed max-h-60 overflow-y-auto pr-1 scrollbar-thin">
                                    {delta.added.map((item, i) => (
                                        <div key={`add-${i}`} className="flex items-start gap-2">
                                            <span className="text-emerald-500 font-extrabold text-[11px] select-none">➕</span>
                                            <span>{item}</span>
                                        </div>
                                    ))}
                                    {delta.modified.map((item, i) => (
                                        <div key={`mod-${i}`} className="flex items-start gap-2">
                                            <span className="text-amber-500 font-extrabold text-[11px] select-none">🔄</span>
                                            <span>{item}</span>
                                        </div>
                                    ))}
                                    {delta.deleted.map((item, i) => (
                                        <div key={`del-${i}`} className="flex items-start gap-2">
                                            <span className="text-red-500 font-extrabold text-[11px] select-none">❌</span>
                                            <span>{item}</span>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-slate-400 dark:text-slate-500 text-[10px] font-semibold italic flex items-center gap-1">
                                    <span className="material-symbols-outlined text-[12px]">ads_click</span>
                                    Haz clic para desplegar los detalles de esta acción.
                                </div>
                            )}
                        </div>
                    </div>
                );
            }

            switch (rec.doc_id) {
                case 'tasks': {
                    if (dataList) {
                        let folderCount = 0;
                        let taskCount = 0;
                        let criticalCount = 0;
                        const taskNames: string[] = [];

                        const countRecursive = (list: any[]) => {
                            list.forEach(t => {
                                if (t.type === 'folder') {
                                    folderCount++;
                                } else {
                                    taskCount++;
                                    if (t.isCritical) criticalCount++;
                                    if (t.name) {
                                        taskNames.push(t.name);
                                    }
                                }
                                if (Array.isArray(t.children)) {
                                    countRecursive(t.children);
                                }
                            });
                        };
                        countRecursive(dataList);

                        if (taskCount === 0 && folderCount === 0) {
                            dataList.forEach((t: any) => {
                                if (t.type === 'folder') folderCount++;
                                else {
                                    taskCount++;
                                    if (t.isCritical) criticalCount++;
                                    if (t.name) taskNames.push(t.name);
                                }
                            });
                        }

                        // Calculate weighted average progress for leaf tasks (duration * progress / total_duration)
                        let totalDuration = 0;
                        let weightedProgressSum = 0;

                        const calcProgressRecursive = (list: any[]) => {
                            list.forEach(t => {
                                if (t.type === 'task' && (!t.children || t.children.length === 0)) {
                                    const dur = t.duration || 0;
                                    const prog = t.progress || 0;
                                    totalDuration += dur;
                                    weightedProgressSum += (dur * prog);
                                }
                                if (Array.isArray(t.children)) {
                                    calcProgressRecursive(t.children);
                                }
                            });
                        };
                        calcProgressRecursive(dataList);

                        const avgProgress = totalDuration > 0 ? (weightedProgressSum / totalDuration) : 0;
                        const displayNames = isRecordExpanded ? taskNames : taskNames.slice(0, 5);

                        return (
                            <div className="space-y-2 mt-1 text-xs text-slate-650 dark:text-slate-300">
                                <div 
                                    onClick={() => setExpandedRecords(prev => ({ ...prev, [rec.id]: !prev[rec.id] }))}
                                    className="flex flex-wrap gap-3 font-bold text-indigo-600 dark:text-indigo-400 cursor-pointer hover:text-indigo-800 dark:hover:text-indigo-300 transition-all select-none items-center"
                                >
                                    <span>📋 Tareas Totales: {taskCount}</span>
                                    <span>📁 Capítulos: {folderCount}</span>
                                    <span>📈 Avance Programado: {avgProgress.toFixed(1)}%</span>
                                    {criticalCount > 0 && <span className="text-red-500 font-extrabold animate-pulse">⚠️ Ruta Crítica: {criticalCount} tareas</span>}
                                    {taskNames.length > 5 && (
                                        <span className="text-[10px] text-indigo-500/80 font-semibold italic bg-indigo-50 dark:bg-indigo-950/30 px-1.5 py-0.5 rounded ml-1">
                                            {isRecordExpanded ? '(clic para contraer)' : '(clic para expandir)'}
                                        </span>
                                    )}
                                </div>
                                <div className="text-[11px] text-slate-500 leading-normal font-medium">
                                    <span className="font-semibold text-slate-700 dark:text-slate-400">Tareas principales:</span> {displayNames.join(', ')}{!isRecordExpanded && taskNames.length > 5 ? '...' : ''}
                                    {taskNames.length > 5 && (
                                        <button 
                                            onClick={() => setExpandedRecords(prev => ({ ...prev, [rec.id]: !prev[rec.id] }))}
                                            className="ml-2 text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300 font-bold hover:underline"
                                        >
                                            {isRecordExpanded ? '▲ Ver menos tareas' : `▼ ... y ${taskNames.length - 5} tareas más (clic para desplegar)`}
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    }
                    break;
                }
                case 'weeklyPlans': {
                    if (dataList) {
                        const total = dataList.length;
                        if (total === 0) return <span className="text-slate-500">Sin planes semanales registrados.</span>;
                        const sliceData = isRecordExpanded ? dataList : dataList.slice(0, 4);

                        return (
                            <div className="space-y-1.5 mt-1 text-xs text-slate-655 dark:text-slate-300">
                                <div 
                                    onClick={() => setExpandedRecords(prev => ({ ...prev, [rec.id]: !prev[rec.id] }))}
                                    className="font-bold text-slate-700 dark:text-slate-400 flex items-center gap-1 cursor-pointer hover:text-indigo-600 dark:hover:text-indigo-350 transition-all select-none"
                                >
                                    <span className="material-symbols-outlined text-[15px] text-indigo-500">calendar_view_week</span>
                                    Contiene {total} Plan(es) Semanal(es) de Producción:
                                    {total > 4 && (
                                        <span className="text-[10px] text-indigo-500/80 font-semibold italic bg-indigo-50 dark:bg-indigo-950/30 px-1.5 py-0.5 rounded ml-1">
                                            {isRecordExpanded ? '(clic para contraer)' : '(clic para expandir)'}
                                        </span>
                                    )}
                                </div>
                                <ul className="list-disc list-inside pl-2 space-y-1 text-slate-500">
                                    {sliceData.map((plan: any, idx: number) => (
                                        <li key={plan.id || idx}>
                                            Plan #{plan.id || (idx + 1)} ({formatDateShort(plan.startDate)} al {formatDateShort(plan.endDate)}) 
                                            | Estado: <span className="px-1.5 py-0.2 rounded text-[10px] bg-slate-100 dark:bg-slate-800 font-bold border border-slate-200/50 dark:border-slate-700">{plan.status || 'Activo'}</span> 
                                            | Asignaciones: <span className="font-bold text-indigo-600 dark:text-indigo-400">{plan.allocations?.length || 0} tareas en terreno</span>
                                        </li>
                                    ))}
                                    {total > 4 && (
                                        <li className="list-none pt-1">
                                            <button 
                                                onClick={() => setExpandedRecords(prev => ({ ...prev, [rec.id]: !prev[rec.id] }))}
                                                className="text-indigo-600 hover:text-indigo-850 dark:text-indigo-400 dark:hover:text-indigo-300 font-bold hover:underline flex items-center gap-1 active:scale-95 transition-all"
                                            >
                                                <span className="material-symbols-outlined text-[16px]">{isRecordExpanded ? 'expand_less' : 'expand_more'}</span>
                                                {isRecordExpanded 
                                                    ? 'Ver menos planificaciones' 
                                                    : `... y ${total - 4} planes semanales adicionales (clic para desplegar)`
                                                }
                                            </button>
                                        </li>
                                    )}
                                </ul>
                            </div>
                        );
                    }
                    break;
                }
                case 'lookaheadPlans': {
                    if (dataList) {
                        const total = dataList.length;
                        if (total === 0) return <span className="text-slate-500">Sin planificaciones intermedias (Lookahead) registradas.</span>;
                        const sliceData = isRecordExpanded ? dataList : dataList.slice(0, 4);

                        return (
                            <div className="space-y-1.5 mt-1 text-xs text-slate-655 dark:text-slate-300">
                                <div 
                                    onClick={() => setExpandedRecords(prev => ({ ...prev, [rec.id]: !prev[rec.id] }))}
                                    className="font-bold text-slate-700 dark:text-slate-400 flex items-center gap-1 cursor-pointer hover:text-amber-600 dark:hover:text-amber-400 transition-all select-none"
                                >
                                    <span className="material-symbols-outlined text-[15px] text-amber-500">view_week</span>
                                    Lookahead (Planificación Intermedia - {total} bloques):
                                    {total > 4 && (
                                        <span className="text-[10px] text-amber-500/80 font-semibold italic bg-amber-50 dark:bg-amber-955/30 px-1.5 py-0.5 rounded ml-1">
                                            {isRecordExpanded ? '(clic para contraer)' : '(clic para expandir)'}
                                        </span>
                                    )}
                                </div>
                                <ul className="list-disc list-inside pl-2 space-y-1 text-slate-500">
                                    {sliceData.map((plan: any, idx: number) => (
                                        <li key={plan.id || idx}>
                                            Semana {plan.weeksAhead ? `${plan.weeksAhead} semanas` : `ID: ${plan.id}`} ({formatDateShort(plan.startDate)} al {formatDateShort(plan.endDate)}) 
                                            | Estado: <span className="underline font-bold text-amber-600">{plan.status || 'En Proceso'}</span> 
                                            | Restricciones activas: <span className="font-bold text-slate-700 dark:text-slate-300">{plan.constraints?.length || 0} ítems</span>
                                        </li>
                                    ))}
                                    {total > 4 && (
                                        <li className="list-none pt-1">
                                            <button 
                                                onClick={() => setExpandedRecords(prev => ({ ...prev, [rec.id]: !prev[rec.id] }))}
                                                className="text-amber-600 hover:text-amber-800 dark:text-amber-400 dark:hover:text-amber-300 font-bold hover:underline flex items-center gap-1 active:scale-95 transition-all"
                                            >
                                                <span className="material-symbols-outlined text-[16px]">{isRecordExpanded ? 'expand_less' : 'expand_more'}</span>
                                                {isRecordExpanded 
                                                    ? 'Ver menos semanas' 
                                                    : `... y ${total - 4} semanas Lookahead adicionales (clic para desplegar)`
                                                }
                                            </button>
                                        </li>
                                    )}
                                </ul>
                            </div>
                        );
                    }
                    break;
                }
                case 'contracts': {
                    if (dataList) {
                        const total = dataList.length;
                        if (total === 0) return <span className="text-slate-500">Sin contratos de subcontratos registrados.</span>;
                        
                        const contractIds: string[] = [];
                        let totalPartidas = 0;

                        dataList.forEach((c: any) => {
                            if (c.id) {
                                contractIds.push(c.id);
                            }
                            if (Array.isArray(c.items)) {
                                totalPartidas += c.items.length;
                            }
                        });

                        const displayIds = isRecordExpanded ? contractIds : contractIds.slice(0, 5);

                        return (
                            <div className="space-y-1.5 mt-1 text-xs text-slate-655 dark:text-slate-300">
                                <div 
                                    onClick={() => setExpandedRecords(prev => ({ ...prev, [rec.id]: !prev[rec.id] }))}
                                    className="font-bold text-slate-700 dark:text-slate-400 flex items-center gap-1 cursor-pointer hover:text-indigo-650 dark:hover:text-indigo-355 transition-all select-none"
                                >
                                    <span className="material-symbols-outlined text-[15px] text-teal-500">handshake</span>
                                    Contratos de Subcontratos ({total} contratos vigentes):
                                    {contractIds.length > 5 && (
                                        <span className="text-[10px] text-indigo-500/80 font-semibold italic bg-indigo-50 dark:bg-indigo-955/30 px-1.5 py-0.5 rounded ml-1">
                                            {isRecordExpanded ? '(clic para contraer)' : '(clic para expandir)'}
                                        </span>
                                    )}
                                </div>
                                <div className="text-[11px] text-slate-500 leading-normal font-medium">
                                    <span className="font-semibold text-slate-700 dark:text-slate-400">Contratos afectados:</span> {displayIds.join(', ')}{!isRecordExpanded && contractIds.length > 5 ? '...' : ''}
                                    {contractIds.length > 5 && (
                                        <button 
                                            onClick={() => setExpandedRecords(prev => ({ ...prev, [rec.id]: !prev[rec.id] }))}
                                            className="ml-2 text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300 font-bold hover:underline block mt-1"
                                        >
                                            {isRecordExpanded ? '▲ Ver menos contratos' : `▼ ... y ${total - 5} contratos más (clic para desplegar)`}
                                        </button>
                                    )}
                                    <br />
                                    <span className="font-semibold text-slate-700 dark:text-slate-400">Total partidas contratadas:</span> {totalPartidas} ítems presupuestarios en ejecución.
                                </div>
                            </div>
                        );
                    }
                    break;
                }
                case 'paymentStatements': {
                    if (dataList) {
                        const total = dataList.length;
                        if (total === 0) return <span className="text-slate-500">Sin estados de pago generados en esta copia.</span>;
                        
                        let totalMontoNeto = 0;
                        const epIds: string[] = [];

                        dataList.forEach((ep: any) => {
                            if (ep.id) epIds.push(ep.id);
                            if (ep.netAmount || ep.montoNeto) totalMontoNeto += (ep.netAmount || ep.montoNeto || 0);
                        });

                        const displayIds = isRecordExpanded ? epIds : epIds.slice(0, 4);

                        return (
                            <div className="space-y-1.5 mt-1 text-xs text-slate-655 dark:text-slate-300">
                                <div 
                                    onClick={() => setExpandedRecords(prev => ({ ...prev, [rec.id]: !prev[rec.id] }))}
                                    className="font-bold text-slate-700 dark:text-slate-400 flex items-center gap-1 cursor-pointer hover:text-emerald-600 dark:hover:text-emerald-450 transition-all select-none"
                                >
                                    <span className="material-symbols-outlined text-[15px] text-emerald-500">payments</span>
                                    Estados de Pago de Subcontratistas ({total} EPs históricos):
                                    {epIds.length > 4 && (
                                        <span className="text-[10px] text-emerald-500/80 font-semibold italic bg-emerald-50 dark:bg-emerald-955/30 px-1.5 py-0.5 rounded ml-1">
                                            {isRecordExpanded ? '(clic para contraer)' : '(clic para expandir)'}
                                        </span>
                                    )}
                                </div>
                                <div className="text-[11px] text-slate-500 leading-normal font-medium">
                                    <span className="font-semibold text-slate-700 dark:text-slate-400">Códigos EP:</span> {displayIds.join(', ')}{!isRecordExpanded && epIds.length > 4 ? '...' : ''}
                                    {epIds.length > 4 && (
                                        <button 
                                            onClick={() => setExpandedRecords(prev => ({ ...prev, [rec.id]: !prev[rec.id] }))}
                                            className="ml-2 text-emerald-600 hover:text-emerald-800 dark:text-emerald-400 dark:hover:text-emerald-300 font-bold hover:underline block mt-1"
                                        >
                                            {isRecordExpanded ? '▲ Ver menos Estados de Pago' : `▼ ... y ${total - 4} Estados de Pago más (clic para desplegar)`}
                                        </button>
                                    )}
                                    <br />
                                    <span className="font-semibold text-slate-700 dark:text-slate-400">Monto total consolidado:</span> ${totalMontoNeto.toLocaleString('es-CL')} CLP netos.
                                </div>
                            </div>
                        );
                    }
                    break;
                }
                case 'savedProtocols': {
                    const keys = payload ? Object.keys(payload) : [];
                    const signedCount = payload && typeof payload === 'object' 
                        ? Object.values(payload).filter((p: any) => p && p.signatures && Object.values(p.signatures).some(s => s)).length
                        : 0;

                    return (
                        <div className="space-y-1.5 mt-1 text-xs text-slate-655 dark:text-slate-300">
                            <div className="font-bold text-slate-700 dark:text-slate-400 flex items-center gap-1">
                                <span className="material-symbols-outlined text-[15px] text-emerald-500">verified</span>
                                Firmas y Estados de Protocolos de Calidad:
                            </div>
                            <div className="text-[11px] text-slate-500">
                                <span className="font-semibold text-slate-700 dark:text-slate-400">Protocolos activos:</span> {keys.length} ítems en control.
                                <br />
                                <span className="font-semibold text-slate-700 dark:text-slate-400">Protocolos con firmas registradas:</span> {signedCount} fichas técnicas firmadas por ITO/Supervisor.
                            </div>
                        </div>
                    );
                }
                case 'protocolConfigs': {
                    const keys = payload ? Object.keys(payload) : [];
                    const activeConfigs = payload && typeof payload === 'object' 
                        ? Object.values(payload).filter((c: any) => c && c.applies).length
                        : 0;

                    return (
                        <div className="space-y-1.5 mt-1 text-xs text-slate-655 dark:text-slate-300">
                            <div className="font-bold text-slate-700 dark:text-slate-400 flex items-center gap-1">
                                <span className="material-symbols-outlined text-[15px] text-teal-500">rule</span>
                                Configuración de Calidad por Tarea:
                            </div>
                            <div className="text-[11px] text-slate-500">
                                <span className="font-semibold text-slate-700 dark:text-slate-400">Tareas configuradas:</span> {keys.length} tareas totales.
                                <br />
                                <span className="font-semibold text-slate-700 dark:text-slate-400">Tareas con protocolo obligatorio:</span> {activeConfigs} tareas activas de aseguramiento de calidad.
                            </div>
                        </div>
                    );
                }
                case 'activities': {
                    if (dataList) {
                        const sliceData = isRecordExpanded ? dataList : dataList.slice(0, 4);
                        return (
                            <div className="space-y-1.5 mt-1 text-xs text-slate-500 animate-in fade-in duration-200">
                                <div 
                                    onClick={() => setExpandedRecords(prev => ({ ...prev, [rec.id]: !prev[rec.id] }))}
                                    className="font-bold text-slate-700 dark:text-slate-400 flex items-center gap-1 cursor-pointer hover:text-indigo-600 dark:hover:text-indigo-400 transition-all select-none"
                                >
                                    <span className="material-symbols-outlined text-[15px] text-indigo-500">assignment</span>
                                    Log de Actividades en Obra ({dataList.length} eventos):
                                    {dataList.length > 4 && (
                                        <span className="text-[10px] text-indigo-500/80 font-semibold italic bg-indigo-50 dark:bg-indigo-955/30 px-1.5 py-0.5 rounded ml-1">
                                            {isRecordExpanded ? '(clic para contraer)' : '(clic para expandir)'}
                                        </span>
                                    )}
                                </div>
                                <ul className="list-disc list-inside pl-2 space-y-1 max-h-48 overflow-y-auto scrollbar-thin">
                                    {sliceData.map((act: any, idx: number) => (
                                        <li key={idx} className="truncate">
                                            [{formatDateShort(act.timestamp || act.date).split(' ')[1] || ''}] <span className="font-semibold">{act.user || act.usuario || 'Sistema'}</span>: {act.description || act.action || act.description_activity || 'Sin descripción'}
                                        </li>
                                    ))}
                                    {dataList.length > 4 && (
                                        <li className="list-none pt-1">
                                            <button 
                                                onClick={() => setExpandedRecords(prev => ({ ...prev, [rec.id]: !prev[rec.id] }))}
                                                className="text-indigo-600 hover:text-indigo-850 dark:text-indigo-400 dark:hover:text-indigo-300 font-bold hover:underline flex items-center gap-1 active:scale-95 transition-all"
                                            >
                                                <span className="material-symbols-outlined text-[16px]">{isRecordExpanded ? 'expand_less' : 'expand_more'}</span>
                                                {isRecordExpanded 
                                                    ? 'Ver menos actividades' 
                                                    : `... y ${dataList.length - 4} actividades adicionales (clic para desplegar)`
                                                }
                                            </button>
                                        </li>
                                    )}
                                </ul>
                            </div>
                        );
                    }
                    break;
                }
            }

            // Fallback genérico robusto
            if (dataList && Array.isArray(dataList)) {
                return <span className="text-slate-500">Historial con {dataList.length} registros (Formato JSON)</span>;
            } else if (payload && typeof payload === 'object') {
                const keys = Object.keys(payload);
                return <span className="text-slate-500">Configuración estructurada ({keys.length} parámetros configurados)</span>;
            }

            return <span className="text-slate-500">Tipo de dato: {typeof payload}</span>;
        } catch (e) {
            return <span className="text-red-400">Error al procesar el contenido de la copia de seguridad</span>;
        }
    };

    const checkBackupsStatus = async () => {
        try {
            // Check if RPC helper exists on Supabase
            const { data, error } = await supabase.rpc('get_backups_status');
            if (error) {
                console.log("RPC get_backups_status not detected. Showing manual DDL instructions.");
                setRpcSupported(false);
            } else {
                setBackupsEnabled(data);
                setRpcSupported(true);
            }
        } catch (err) {
            console.log("Error querying get_backups_status RPC:", err);
            setRpcSupported(false);
        }
    };

    const handleToggleBackups = async () => {
        if (backupsEnabled === null) return;
        
        const actionStr = backupsEnabled ? 'DESACTIVAR' : 'ACTIVAR';
        const confirmMsg = backupsEnabled 
            ? '⚠️ ¿Estás seguro de que deseas DESACTIVAR la creación de respaldos automáticos futuros?\n\nEl sistema dejará de guardar copias históricas de tus tablas para ahorrar recursos y almacenamiento.'
            : '¿Estás seguro de que deseas ACTIVAR la creación de respaldos automáticos futuros?';
            
        if (!window.confirm(confirmMsg)) {
            return;
        }

        setIsUpdatingStatus(true);
        try {
            // Call Supabase RPC function (needs to be created by the user once)
            const { error } = await supabase.rpc('toggle_backups', { disable_backups: backupsEnabled });
            if (error) {
                console.error("Error toggling backups:", error);
                alert("Error al cambiar estado en base de datos: " + error.message);
            } else {
                const nextState = !backupsEnabled;
                setBackupsEnabled(nextState);
                alert(`¡Respaldos automáticos futuros ${nextState ? 'ACTIVADOS' : 'DESACTIVADOS'} con éxito!`);
            }
        } catch (err: any) {
            console.error("Exception toggling backups:", err);
            alert("Error crítico al cambiar estado: " + err.message);
        } finally {
            setIsUpdatingStatus(false);
        }
    };

    const fetchBackups = async () => {
        setIsLoading(true);
        try {
            // Fetch current rows from app_data
            const { data: activeRows, error: activeError } = await supabase
                .from('app_data')
                .select('collection_name, doc_id, payload');
            
            if (activeError) {
                console.error("Error fetching current rows:", activeError);
            } else if (activeRows) {
                setCurrentRows(activeRows);
            }

            let query = supabase
                .from('app_data_history')
                .select('*')
                .order('changed_at', { ascending: false })
                .limit(200);

            if (filterDocId) {
                query = query.ilike('doc_id', `%${filterDocId}%`);
            }

            const { data, error } = await query;
            if (error) {
                console.error("Error fetching backups:", error);
            } else if (data) {
                setBackups(data);
                groupAndSetPackages(data);
            }
        } catch (err) {
            console.error("Fetch backups exception:", err);
        } finally {
            setIsLoading(false);
        }
    };

    // Resolve backup author using internal payload data or historical activities correlation
    const resolveBackupAuthor = (rec: BackupRecord): string => {
        // First, check active activities row in currentRows (real-time up-to-date log)
        const activeActivitiesRow = currentRows.find(
            r => r.doc_id === 'activities' && r.collection_name === rec.collection_name
        );
        const backupTime = new Date(rec.changed_at).getTime();
        let closestUser = '';
        let minDiff = 10 * 60 * 1000; // 10 minutes window

        if (activeActivitiesRow && activeActivitiesRow.payload && Array.isArray(activeActivitiesRow.payload.list)) {
            activeActivitiesRow.payload.list.forEach((act: any) => {
                const actTimeStr = act.timestamp || act.date;
                if (actTimeStr) {
                    const actTime = new Date(actTimeStr).getTime();
                    const diff = Math.abs(backupTime - actTime);
                    if (diff < minDiff) {
                        minDiff = diff;
                        closestUser = act.user || act.usuario || '';
                    }
                }
            });
        }

        if (closestUser) return closestUser;

        // Resolve author from the NEW state (the state after this change)
        const newState = getNewStatePayload(rec);
        if (newState) {
            if (newState.updatedBy) return newState.updatedBy;
            if (newState.user || newState.usuario) return newState.user || newState.usuario;
            if (newState.author) return newState.author;
            if (Array.isArray(newState.list) && newState.list.length > 0) {
                const first = newState.list[0];
                if (first.user || first.usuario) return first.user || first.usuario;
            }
        }

        // Fallback to old state payload just in case
        if (rec.payload) {
            const p = rec.payload;
            if (p.updatedBy) return p.updatedBy;
            if (p.user || p.usuario) return p.user || p.usuario;
            if (p.author) return p.author;
            
            if (Array.isArray(p.list) && p.list.length > 0) {
                const first = p.list[0];
                if (first.user || first.usuario) return first.user || first.usuario;
            }
        }

        // Correlate with project activity history (loaded in standard backups state)
        const actRecords = backups.filter(b => b.doc_id === 'activities' && b.collection_name === rec.collection_name);

        actRecords.forEach(actRec => {
            if (actRec.payload && Array.isArray(actRec.payload.list)) {
                actRec.payload.list.forEach((act: any) => {
                    const actTimeStr = act.timestamp || act.date;
                    if (actTimeStr) {
                        const actTime = new Date(actTimeStr).getTime();
                        const diff = Math.abs(backupTime - actTime);
                        if (diff < minDiff) {
                            minDiff = diff;
                            closestUser = act.user || act.usuario || '';
                        }
                    }
                });
            }
        });

        if (closestUser) return closestUser;

        // Eloquent fallback roles
        if (rec.doc_id === 'tasks' || rec.doc_id === 'levels' || rec.doc_id === 'units') return "Administrador de Planificación";
        if (rec.doc_id === 'weeklyPlans' || rec.doc_id === 'lookaheadPlans') return "Controlador de Producción";
        if (rec.doc_id === 'contracts' || rec.doc_id === 'paymentStatements') return "Administrador de Contratos";
        if (rec.doc_id === 'savedProtocols' || rec.doc_id === 'protocolConfigs') return "Inspector de Calidad";

        return "Usuario del Sistema";
    };

    const groupAndSetPackages = (records: BackupRecord[]) => {
        const packagesMap: Record<string, BackupRecord[]> = {};

        records.forEach(rec => {
            // Noise exclusion filters (removes user directory, incidents, configurations, etc.)
            if (
                rec.doc_id === 'globalUsers' ||
                rec.doc_id === 'incidents' ||
                rec.doc_id === 'incidencias' ||
                rec.doc_id === 'companyConfig' ||
                rec.collection_name === 'data'
            ) {
                return;
            }

            // Exclude single-attribute cryptic nodes
            if (rec.payload && typeof rec.payload === 'object' && !Array.isArray(rec.payload)) {
                const keys = Object.keys(rec.payload);
                if (keys.length === 1 && (keys[0] === 'id' || keys[0] === 'updated_at' || keys[0] === 'collection_name')) {
                    return;
                }
            }

            const date = new Date(rec.changed_at);
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hour = String(date.getHours()).padStart(2, '0');
            const key = `${day}/${month}/${year} ${hour}:00 hrs`;

            if (!packagesMap[key]) {
                packagesMap[key] = [];
            }
            packagesMap[key].push(rec);
        });

        const grouped = Object.keys(packagesMap).map(key => {
            const recordsInPackage = packagesMap[key];
            recordsInPackage.sort((a, b) => new Date(b.changed_at).getTime() - new Date(a.changed_at).getTime());
            return {
                packageId: key,
                timestamp: recordsInPackage[0].changed_at,
                records: recordsInPackage
            };
        }).filter(pkg => pkg.records.length > 0)
          .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

        setPackages(grouped);
    };

    useEffect(() => {
        fetchBackups();
        checkBackupsStatus();
    }, [filterDocId]);

    const togglePackage = (pkgId: string) => {
        setExpandedPackages(prev => ({
            ...prev,
            [pkgId]: !prev[pkgId]
        }));
    };

    // Triggers custom interactive confirmation modal for single element restore
    const handleRestoreElement = (record: BackupRecord) => {
        setConfirmModal({
            isOpen: true,
            record,
            isPackage: false,
            packageData: null,
            onConfirm: () => executeRestoreElement(record)
        });
    };

    const executeRestoreElement = async (record: BackupRecord) => {
        setIsRestoring(record.id);
        const humanName = getHumanReadableDocId(record.doc_id);
        try {
            const { error } = await supabase
                .from('app_data')
                .upsert({
                    collection_name: record.collection_name,
                    doc_id: record.doc_id,
                    payload: record.payload,
                    updated_at: new Date().toISOString()
                }, { onConflict: 'collection_name,doc_id' });

            if (error) {
                console.error("Error restoring backup:", error);
                alert("Error al restaurar: " + error.message);
            } else {
                alert(`¡Restauración exitosa! "${humanName}" ha vuelto a su estado anterior.`);
            }
        } catch (err: any) {
            console.error("Exception restoring backup:", err);
            alert("Error crítico al restaurar: " + err.message);
        } finally {
            setIsRestoring(null);
            setConfirmModal(prev => ({ ...prev, isOpen: false }));
        }
    };

    // Triggers custom interactive confirmation modal for package restore
    const handleRestorePackage = (pkg: BackupPackage) => {
        setConfirmModal({
            isOpen: true,
            record: null,
            isPackage: true,
            packageData: pkg,
            onConfirm: () => executeRestorePackage(pkg)
        });
    };

    const executeRestorePackage = async (pkg: BackupPackage) => {
        setIsRestoring(`pkg-${pkg.packageId}`);
        let successCount = 0;
        let failCount = 0;

        for (const record of pkg.records) {
            try {
                const { error } = await supabase
                    .from('app_data')
                    .upsert({
                        collection_name: record.collection_name,
                        doc_id: record.doc_id,
                        payload: record.payload,
                        updated_at: new Date().toISOString()
                    }, { onConflict: 'collection_name,doc_id' });

                if (error) {
                    console.error(`Error in batch restore for ${record.doc_id}:`, error);
                    failCount++;
                } else {
                    successCount++;
                }
            } catch (err) {
                console.error(`Exception in batch restore for ${record.doc_id}:`, err);
                failCount++;
            }
        }

        if (failCount === 0) {
            alert(`¡Restauración del paquete completada con éxito! Se restauraron ${successCount} elementos.`);
        } else {
            alert(`Restauración parcial: ${successCount} completados con éxito y ${failCount} fallidos. Revise la consola.`);
        }
        setIsRestoring(null);
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
    };

    // DELETE a single backup record from the history table (Clean up)
    const handleDeleteRecord = async (record: BackupRecord) => {
        const humanName = getHumanReadableDocId(record.doc_id);
        const dateStr = new Date(record.changed_at).toLocaleString('es-CL');
        if (!window.confirm(`¿Estás seguro de que deseas ELIMINAR permanentemente este respaldo de "${humanName}" (${dateStr})?\n\nEsta copia se borrará del historial para mantener el sistema limpio. No afectará los datos actuales.`)) {
            return;
        }

        setIsRestoring(record.id);
        try {
            const { error } = await supabase
                .from('app_data_history')
                .delete()
                .eq('id', record.id);

            if (error) {
                console.error("Error deleting backup record:", error);
                alert("Error al eliminar del historial: " + error.message);
            } else {
                const updated = backups.filter(b => b.id !== record.id);
                setBackups(updated);
                groupAndSetPackages(updated);
            }
        } catch (err: any) {
            console.error("Exception deleting backup record:", err);
            alert("Error crítico al eliminar del historial: " + err.message);
        } finally {
            setIsRestoring(null);
        }
    };

    // DELETE an entire hourly package from history
    const handleDeletePackage = async (pkg: BackupPackage) => {
        const dateStr = pkg.packageId;
        const affectedCount = pkg.records.length;
        if (!window.confirm(`¿Estás seguro de que deseas ELIMINAR permanentemente todas las copias (${affectedCount}) del punto de restauración del ${dateStr}?\n\nSe borrarán del historial para mantener limpio el panel. Esta acción no se puede deshacer.`)) {
            return;
        }

        setIsRestoring(`del-pkg-${pkg.packageId}`);
        const idsToDelete = pkg.records.map(r => r.id);
        try {
            const { error } = await supabase
                .from('app_data_history')
                .delete()
                .in('id', idsToDelete);

            if (error) {
                console.error("Error deleting backup package:", error);
                alert("Error al eliminar el paquete: " + error.message);
            } else {
                const updated = backups.filter(b => !idsToDelete.includes(b.id));
                setBackups(updated);
                groupAndSetPackages(updated);
            }
        } catch (err: any) {
            console.error("Exception deleting backup package:", err);
            alert("Error crítico al eliminar el paquete: " + err.message);
        } finally {
            setIsRestoring(null);
        }
    };

    // PURGE the entire backups history table (app_data_history)
    const handlePurgeHistory = async () => {
        if (!window.confirm("⚠️ ADVERTENCIA CRÍTICA: ¿Estás seguro de que deseas ELIMINAR TODO EL HISTORIAL de copias de seguridad de la base de datos?\n\nEsta acción vaciará permanentemente todas las copias almacenadas. Ninguna versión anterior de ninguna obra podrá ser recuperada en el futuro.")) {
            return;
        }

        const confirmText = window.prompt("Para confirmar la purga absoluta del historial, escribe 'PURGAR TODO' en mayúsculas:");
        if (confirmText !== "PURGAR TODO") {
            alert("Confirmación incorrecta. Acción cancelada.");
            return;
        }

        setIsLoading(true);
        try {
            const { error } = await supabase
                .from('app_data_history')
                .delete()
                .neq('id', '00000000-0000-0000-0000-000000000000'); // Delete everything in Postgres

            if (error) {
                console.error("Error purging backups history:", error);
                alert("Error al purgar el historial: " + error.message);
            } else {
                alert("¡El historial de copias de seguridad ha sido purgado por completo!");
                setBackups([]);
                setPackages([]);
            }
        } catch (err: any) {
            console.error("Exception purging backups history:", err);
            alert("Error crítico al purgar el historial: " + err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const formatDate = (isoString: string) => {
        const date = new Date(isoString);
        return date.toLocaleString('es-CL', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit', second: '2-digit'
        });
    };

    return (
        <div className="p-4 md:p-8 space-y-6">
            {/* Header section with rich aesthetics */}
            <div className="flex flex-col md:flex-row md:justify-between items-start md:items-center gap-4 bg-white dark:bg-[#1a2634] p-6 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm">
                <div className="flex items-center gap-4">
                    <div className="flex items-center justify-center rounded-2xl bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 size-12 shadow-sm">
                        <span className="material-symbols-outlined text-[28px]">history</span>
                    </div>
                    <div>
                        <h2 className="text-xl font-bold text-slate-800 dark:text-white leading-tight">
                            Puntos de Restauración y Limpieza del Sistema
                        </h2>
                        <p className="text-xs text-slate-500 mt-1 max-w-xl">
                            Administra de forma centralizada la integridad de los datos. Puedes restaurar versiones previas, eliminar respaldos específicos para mantener el historial limpio, o apagar la generación de futuras copias.
                        </p>
                    </div>
                </div>
                <div className="flex flex-wrap gap-2 shrink-0">
                    <button 
                        onClick={() => setShowTriggerPanel(!showTriggerPanel)}
                        className="bg-white hover:bg-slate-50 text-slate-700 dark:bg-slate-800 dark:hover:bg-slate-700 dark:text-slate-300 px-4 py-2.5 text-xs rounded-xl font-bold flex items-center gap-2 transition-all shadow-sm border border-slate-200 dark:border-slate-700"
                    >
                        <span className="material-symbols-outlined text-[16px] text-amber-500">settings</span>
                        Controlar Futuros
                    </button>
                    <button 
                        onClick={handlePurgeHistory}
                        disabled={backups.length === 0 || isLoading}
                        className="bg-red-50 hover:bg-red-500 hover:text-white text-red-600 dark:bg-red-950/20 dark:hover:bg-red-600 px-4 py-2.5 text-xs rounded-xl font-bold flex items-center gap-2 transition-all shadow-sm border border-red-200 dark:border-red-900/50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <span className="material-symbols-outlined text-[16px]">delete_sweep</span>
                        Purga Completa
                    </button>
                    <button 
                        onClick={fetchBackups}
                        className="bg-slate-100 hover:bg-slate-200 text-slate-700 dark:bg-slate-800 dark:hover:bg-slate-700 dark:text-slate-300 px-4 py-2.5 text-xs rounded-xl font-bold flex items-center gap-2 transition-all shadow-sm border border-slate-200 dark:border-slate-700"
                    >
                        <span className="material-symbols-outlined text-[16px]">refresh</span>
                        Actualizar
                    </button>
                </div>
            </div>

            {/* Reassuring Safety Banner (Garantía de Aislamiento) */}
            <div className="bg-indigo-50/70 dark:bg-indigo-950/20 border border-indigo-200/50 dark:border-indigo-900/40 p-5 rounded-3xl flex items-start gap-4 shadow-sm">
                <div className="flex items-center justify-center rounded-2xl bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400 size-12 shrink-0">
                    <span className="material-symbols-outlined text-[28px]">shield_check</span>
                </div>
                <div className="space-y-1">
                    <h4 className="font-bold text-sm text-indigo-900 dark:text-indigo-200 uppercase tracking-wider flex items-center gap-1.5">
                        🛡️ Garantía de Aislamiento y Seguridad en Restauración
                    </h4>
                    <p className="text-xs text-slate-600 dark:text-indigo-300/80 leading-relaxed">
                        Cada punto de restauración está <strong>estrictamente aislado</strong>. Al hacer clic en <strong>Restaurar</strong>, el sistema solo sobrescribirá el archivo o documento seleccionado (por ejemplo, únicamente las planificaciones de esa obra específica) sin alterar de ninguna forma otras obras, presupuestos, subcontratos o configuraciones globales del sistema. Su base de datos permanecerá 100% íntegra y protegida.
                    </p>
                </div>
            </div>

            {/* SQL Trigger Control Panel for Future Backups */}
            {showTriggerPanel && (
                <div className="bg-slate-50 dark:bg-[#151f2b] p-6 rounded-3xl border border-amber-300/40 dark:border-amber-900/30 animate-in fade-in slide-in-from-top-3 duration-200 space-y-4">
                    <div className="flex justify-between items-start">
                        <div className="flex items-center gap-2.5 text-amber-600 dark:text-amber-400">
                            <span className="material-symbols-outlined">settings_suggest</span>
                            <h3 className="font-bold text-sm uppercase tracking-wider">Control de Respaldos Futuros (Base de Datos)</h3>
                        </div>
                        <button 
                            onClick={() => setShowTriggerPanel(false)}
                            className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                        >
                            <span className="material-symbols-outlined text-[20px]">close</span>
                        </button>
                    </div>

                    {rpcSupported && backupsEnabled !== null ? (
                        /* INTEGRATED LIVE TOGGLE CARD (WOW AESTHETIC) */
                        <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 flex flex-col md:flex-row md:items-center justify-between gap-6 shadow-sm">
                            <div className="space-y-1 max-w-xl">
                                <div className="flex items-center gap-2">
                                    <span className={`inline-block size-2 rounded-full ${backupsEnabled ? 'bg-emerald-500 animate-pulse' : 'bg-slate-400'}`} />
                                    <span className="text-xs font-black uppercase tracking-wider text-slate-500">
                                        Estado del Disparador (Trigger)
                                    </span>
                                </div>
                                <h4 className="font-bold text-slate-800 dark:text-white text-base">
                                    Copias de Seguridad Automáticas: {backupsEnabled ? 'ACTIVADAS' : 'DESACTIVADAS'}
                                </h4>
                                <p className="text-xs text-slate-500 leading-relaxed">
                                    {backupsEnabled 
                                        ? 'El sistema está registrando un punto de restauración histórico en tiempo real de cada cambio en los datos para máxima seguridad. Desactívalo si prefieres mantener tu base de datos limpia y ahorrar recursos de base de datos.'
                                        : 'Los respaldos automáticos en tiempo real están suspendidos. No se escribirán nuevas versiones históricas. El historial de datos se mantendrá limpio y sin consumo de almacenamiento adicional.'
                                    }
                                </p>
                            </div>
                            <div className="shrink-0 flex items-center">
                                <button
                                    onClick={handleToggleBackups}
                                    disabled={isUpdatingStatus}
                                    className={`w-full md:w-auto px-6 py-3 rounded-xl font-bold text-xs shadow transition-all flex items-center justify-center gap-2 active:scale-95 ${
                                        backupsEnabled
                                        ? 'bg-red-50 hover:bg-red-500 hover:text-white text-red-600 border border-red-200 dark:border-red-950/20'
                                        : 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-indigo-600/10'
                                    } disabled:opacity-50`}
                                >
                                    <span className="material-symbols-outlined text-[16px]">
                                        {isUpdatingStatus ? 'hourglass_empty' : backupsEnabled ? 'toggle_on' : 'toggle_off'}
                                    </span>
                                    {isUpdatingStatus ? 'Procesando...' : backupsEnabled ? 'Desactivar Respaldos Futuros' : 'Activar Respaldos Futuros'}
                                </button>
                            </div>
                        </div>
                    ) : (
                        /* FALLBACK & INSTRUCTIONS TABS */
                        <div className="space-y-4">
                            <div className="flex border-b border-slate-200 dark:border-slate-800 pb-1 gap-4">
                                <button 
                                    onClick={() => setActiveTriggerTab('manual')}
                                    className={`pb-2 text-xs font-bold transition-all relative ${activeTriggerTab === 'manual' ? 'text-amber-500' : 'text-slate-400 hover:text-slate-600'}`}
                                >
                                    Consola de Comandos SQL
                                    {activeTriggerTab === 'manual' && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-amber-500 rounded-full" />}
                                </button>
                                <button 
                                    onClick={() => setActiveTriggerTab('rpc')}
                                    className={`pb-2 text-xs font-bold transition-all relative ${activeTriggerTab === 'rpc' ? 'text-amber-500' : 'text-slate-400 hover:text-slate-600'}`}
                                >
                                    ✨ Habilitar Control Integrado (Recomendado)
                                    {activeTriggerTab === 'rpc' && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-amber-500 rounded-full" />}
                                </button>
                            </div>

                            {activeTriggerTab === 'manual' ? (
                                <div className="space-y-3">
                                    <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed max-w-3xl">
                                        Las copias se generan mediante un disparador (trigger) a nivel de base de datos cada vez que la aplicación escribe información. Para apagar permanentemente o reactivar la generación de futuras copias, ejecuta los siguientes comandos SQL en el **SQL Editor** de tu consola de Supabase:
                                    </p>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-1">
                                        <div className="bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 space-y-2">
                                            <span className="text-[10px] font-black text-red-500 uppercase tracking-wider block">🚫 DESACTIVAR RESPALDOS FUTUROS:</span>
                                            <code className="block text-xs font-mono bg-slate-50 dark:bg-slate-950 p-2.5 rounded border border-slate-200/50 dark:border-slate-800 text-slate-700 dark:text-slate-300 select-all">
                                                ALTER TABLE app_data DISABLE TRIGGER trg_app_data_history;
                                            </code>
                                        </div>
                                        <div className="bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 space-y-2">
                                            <span className="text-[10px] font-black text-green-600 uppercase tracking-wider block">✅ ACTIVAR RESPALDOS FUTUROS (Por defecto):</span>
                                            <code className="block text-xs font-mono bg-slate-50 dark:bg-slate-950 p-2.5 rounded border border-slate-200/50 dark:border-slate-800 text-slate-700 dark:text-slate-300 select-all">
                                                ALTER TABLE app_data ENABLE TRIGGER trg_app_data_history;
                                            </code>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed max-w-3xl">
                                        ¿Quieres habilitar el botón interactivo directamente en este panel para no tener que usar la consola de comandos? Copia y ejecuta el siguiente script una sola vez en el **SQL Editor** de tu consola de Supabase. Esto creará los puentes de comunicación (RPC) para que puedas prender y apagar los respaldos con un solo clic:
                                    </p>
                                    <div className="bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 space-y-2">
                                        <span className="text-[10px] font-black text-indigo-500 uppercase tracking-wider block">Script de Integración SQL:</span>
                                        <pre className="block text-[11px] font-mono bg-slate-50 dark:bg-slate-950 p-3 rounded border border-slate-200/50 dark:border-slate-800 text-slate-700 dark:text-slate-300 select-all overflow-x-auto max-h-48 scrollbar-thin">
{`-- 1. Crear función para consultar el estado del trigger
CREATE OR REPLACE FUNCTION get_backups_status()
RETURNS BOOLEAN
SECURITY DEFINER
AS $$
DECLARE
    v_enabled CHAR;
BEGIN
    SELECT tgenabled INTO v_enabled
    FROM pg_trigger
    WHERE tgname = 'trg_app_data_history';
    
    RETURN COALESCE(v_enabled != 'D', TRUE);
EXCEPTION
    WHEN OTHERS THEN
        RETURN TRUE;
END;
$$ LANGUAGE plpgsql;

-- 2. Crear función para cambiar el estado del trigger
CREATE OR REPLACE FUNCTION toggle_backups(disable_backups BOOLEAN)
RETURNS VOID
SECURITY DEFINER
AS $$
BEGIN
    IF disable_backups THEN
        EXECUTE 'ALTER TABLE app_data DISABLE TRIGGER trg_app_data_history';
    ELSE
        EXECUTE 'ALTER TABLE app_data ENABLE TRIGGER trg_app_data_history';
    END IF;
END;
$$ LANGUAGE plpgsql;`}
                                        </pre>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Filter Input */}
            <div className="bg-white dark:bg-[#1a2634] p-4 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm">
                <div className="relative">
                    <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">search</span>
                    <input 
                        type="text" 
                        placeholder="Buscar por ID del elemento afectado (ej: tasks, matrixData)..." 
                        value={filterDocId}
                        onChange={(e) => setFilterDocId(e.target.value)}
                        className="w-full pl-10 pr-4 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-sm outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all text-slate-800 dark:text-white"
                    />
                </div>
            </div>

            {/* Grouped Hourly Packages List */}
            <div className="space-y-4">
                {isLoading ? (
                    <div className="p-16 text-center text-slate-400 flex flex-col items-center justify-center bg-white dark:bg-[#1a2634] rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm">
                        <span className="material-symbols-outlined text-4xl animate-spin text-indigo-500 mb-2">sync</span>
                        <p className="font-medium text-sm">Procesando y agrupando puntos de restauración...</p>
                    </div>
                ) : packages.length === 0 ? (
                    <div className="p-16 text-center bg-white dark:bg-[#1a2634] rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm text-slate-500 flex flex-col items-center">
                        <span className="material-symbols-outlined text-5xl mb-3 text-slate-300 dark:text-slate-700">cloud_done</span>
                        <p className="font-bold">No hay puntos de restauración en el historial.</p>
                        <p className="text-xs mt-1 text-slate-400">El historial se encuentra completamente vacío y limpio.</p>
                    </div>
                ) : (
                    packages.map(pkg => {
                        const isPkgExpanded = !!expandedPackages[pkg.packageId];
                        const totalSize = pkg.records.reduce((sum, rec) => sum + JSON.stringify(rec.payload).length, 0);
                        const sizeInKb = Math.round(totalSize / 1024);
                        const isPkgRestoring = isRestoring === `pkg-${pkg.packageId}`;
                        const isPkgDeleting = isRestoring === `del-pkg-${pkg.packageId}`;

                        return (
                            <div 
                                key={pkg.packageId}
                                className={`bg-white dark:bg-[#1a2634] rounded-2xl border transition-all duration-200 shadow-sm overflow-hidden ${
                                    isPkgExpanded 
                                    ? 'border-indigo-500/30 ring-1 ring-indigo-500/10' 
                                    : 'border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700'
                                }`}
                            >
                                {/* Package Master Row */}
                                <div 
                                    onClick={() => togglePackage(pkg.packageId)}
                                    className="p-5 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 cursor-pointer select-none"
                                >
                                    <div className="flex items-center gap-4">
                                        <button className="flex items-center justify-center rounded-xl bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 size-10">
                                            <span className="material-symbols-outlined text-[20px]">
                                                restore
                                            </span>
                                        </button>
                                        <div>
                                            <div className="font-bold text-slate-800 dark:text-white flex items-center gap-2">
                                                <span>Punto de Restauración - {pkg.packageId}</span>
                                            </div>
                                            <div className="flex flex-wrap gap-2 items-center mt-1 text-xs text-slate-500">
                                                <span className="font-semibold text-slate-600 dark:text-slate-400">
                                                    {pkg.records.length} {pkg.records.length === 1 ? 'elemento afectado' : 'elementos afectados'}
                                                </span>
                                                <span className="text-slate-300 dark:text-slate-700">•</span>
                                                <span>Tamaño total: {sizeInKb} KB</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
                                        {/* Restore entire package */}
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleRestorePackage(pkg);
                                            }}
                                            disabled={isPkgRestoring || isPkgDeleting || isRestoring !== null}
                                            className={`px-3 py-2 text-xs font-bold rounded-xl shadow-sm transition-all flex items-center gap-1.5 ${
                                                isPkgRestoring
                                                ? 'bg-amber-100 text-amber-600 cursor-not-allowed'
                                                : isRestoring !== null
                                                    ? 'bg-slate-50 text-slate-400 dark:bg-slate-800 cursor-not-allowed'
                                                    : 'bg-indigo-600 text-white hover:bg-indigo-700 active:scale-95'
                                            }`}
                                        >
                                            <span className="material-symbols-outlined text-[16px]">
                                                {isPkgRestoring ? 'hourglass_empty' : 'settings_backup_restore'}
                                            </span>
                                            {isPkgRestoring ? 'Restaurando...' : 'Restaurar'}
                                        </button>

                                        {/* Delete entire package (Clean up) */}
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDeletePackage(pkg);
                                            }}
                                            disabled={isPkgRestoring || isPkgDeleting || isRestoring !== null}
                                            className={`px-3 py-2 text-xs font-bold rounded-xl shadow-sm transition-all flex items-center gap-1.5 ${
                                                isPkgDeleting
                                                ? 'bg-red-100 text-red-600 cursor-not-allowed'
                                                : isRestoring !== null
                                                    ? 'bg-slate-50 text-slate-400 dark:bg-slate-800 cursor-not-allowed'
                                                    : 'bg-red-50 text-red-600 hover:bg-red-600 hover:text-white border border-red-200 dark:border-red-950/20 active:scale-95'
                                            }`}
                                            title="Eliminar este bloque horario del historial"
                                        >
                                            <span className="material-symbols-outlined text-[16px]">
                                                {isPkgDeleting ? 'hourglass_empty' : 'delete'}
                                            </span>
                                            {isPkgDeleting ? 'Borrando...' : 'Eliminar'}
                                        </button>

                                        <span className={`material-symbols-outlined text-slate-400 transition-transform ${isPkgExpanded ? 'rotate-180' : ''}`}>
                                            expand_more
                                        </span>
                                    </div>
                                </div>

                                {/* Expandable Detail Area */}
                                {isPkgExpanded && (
                                    <div className="bg-slate-50/50 dark:bg-[#151f2b]/50 border-t border-slate-100 dark:border-slate-800/80 p-5 space-y-4 animate-in slide-in-from-top-2 duration-200">
                                        <h4 className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest flex items-center gap-1.5">
                                            <span className="material-symbols-outlined text-[14px]">checklist</span>
                                            Detalle de Elementos Modificados en esta Hora:
                                        </h4>
                                        <div className="space-y-3">
                                            {pkg.records.map((rec) => {
                                                const recordSize = Math.round(JSON.stringify(rec.payload).length / 1024);
                                                const isRecordRestoring = isRestoring === rec.id;
                                                return (
                                                    <div 
                                                        key={rec.id} 
                                                        className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-4 rounded-xl bg-white dark:bg-[#1a2634] border border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700 transition-colors gap-4"
                                                    >
                                                        <div className="flex-1 min-w-0">
                                                            <div className="font-bold text-sm text-slate-700 dark:text-slate-200">
                                                                {getHumanReadableDocId(rec.doc_id)}
                                                            </div>
                                                            <div className="flex flex-wrap gap-2 items-center mt-1 text-[10px] text-slate-400 uppercase tracking-wider font-semibold">
                                                                <span className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 border border-slate-200/50 dark:border-slate-700 font-mono text-[9px]">
                                                                    {rec.doc_id}
                                                                </span>
                                                                <span>•</span>
                                                                <span className="text-indigo-600 dark:text-indigo-400">
                                                                    {getHumanReadableCollection(rec.collection_name)}
                                                                </span>
                                                                <span>•</span>
                                                                <span className="text-slate-600 dark:text-slate-400">
                                                                    👤 {resolveBackupAuthor(rec)}
                                                                </span>
                                                                <span>•</span>
                                                                <span>{recordSize} KB</span>
                                                                <span>•</span>
                                                                <span>{formatDate(rec.changed_at).split(' ')[1]} hrs</span>
                                                            </div>
                                                            <div className="mt-2.5 p-3 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-100/50 dark:border-slate-800/50 text-xs leading-relaxed text-slate-600 dark:text-slate-300">
                                                                {renderPayloadDetails(rec)}
                                                            </div>
                                                        </div>
                                                        <div className="flex gap-2 shrink-0 w-full sm:w-auto justify-end">
                                                            {/* Restore Single element */}
                                                            <button 
                                                                onClick={() => handleRestoreElement(rec)}
                                                                disabled={isRecordRestoring || isRestoring !== null}
                                                                className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all shadow-sm flex items-center gap-1.5 ${
                                                                    isRecordRestoring 
                                                                    ? 'bg-amber-100 text-amber-600 cursor-not-allowed' 
                                                                    : isRestoring !== null
                                                                        ? 'bg-slate-50 text-slate-400 dark:bg-slate-800 cursor-not-allowed'
                                                                        : 'bg-amber-50 text-amber-700 hover:bg-amber-500 hover:text-white border border-amber-200 hover:border-amber-500 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800/50 dark:hover:bg-amber-600 dark:hover:text-white active:scale-95'
                                                                }`}
                                                            >
                                                                <span className="material-symbols-outlined text-[14px]">
                                                                    {isRecordRestoring ? 'hourglass_empty' : 'settings_backup_restore'}
                                                                </span>
                                                                Restaurar
                                                            </button>

                                                            {/* Delete Single element (Clean up) */}
                                                            <button 
                                                                onClick={() => handleDeleteRecord(rec)}
                                                                disabled={isRecordRestoring || isRestoring !== null}
                                                                className={`p-1.5 text-xs font-bold rounded-lg transition-all shadow-sm flex items-center justify-center ${
                                                                    isRestoring !== null
                                                                    ? 'text-slate-300 dark:text-slate-700 cursor-not-allowed'
                                                                    : 'text-red-500 hover:text-white hover:bg-red-500 border border-transparent hover:border-red-500 dark:hover:bg-red-600 transition-colors'
                                                                }`}
                                                                title="Eliminar este respaldo individual"
                                                            >
                                                                <span className="material-symbols-outlined text-[16px]">
                                                                    delete
                                                                </span>
                                                            </button>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </div>

            {/* Premium Confirm Restoration React Modal */}
            {confirmModal.isOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-white dark:bg-[#1a2634] w-full max-w-lg rounded-3xl border border-slate-200 dark:border-slate-800 shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
                        {/* Modal Header */}
                        <div className="p-6 bg-gradient-to-r from-indigo-50 to-indigo-100/50 dark:from-indigo-950/20 dark:to-indigo-950/10 border-b border-slate-200 dark:border-slate-800/80 flex items-center gap-4">
                            <div className="flex items-center justify-center rounded-2xl bg-indigo-600 text-white size-12 shadow-md shadow-indigo-600/20 shrink-0">
                                <span className="material-symbols-outlined text-[28px]">shield_check</span>
                            </div>
                            <div>
                                <h3 className="text-lg font-black text-slate-800 dark:text-white leading-tight">
                                    Confirmar Restauración
                                </h3>
                                <p className="text-xs text-slate-500 mt-0.5">
                                    Respaldo Seguro e Inteligente • K-LyDapp
                                </p>
                            </div>
                        </div>

                        {/* Modal Content */}
                        <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto scrollbar-thin">
                            <div className="space-y-2.5">
                                <div className="text-xs text-slate-400 uppercase tracking-widest font-black">
                                    Detalle del Elemento
                                </div>
                                <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-900 border border-slate-100 dark:border-slate-800 space-y-2.5">
                                    <div className="font-extrabold text-sm text-slate-800 dark:text-white flex items-center gap-1.5">
                                        <span className="material-symbols-outlined text-[18px] text-indigo-500">
                                            {confirmModal.isPackage ? 'inventory_2' : 'history'}
                                        </span>
                                        {confirmModal.isPackage 
                                            ? `Paquete Horario: ${confirmModal.packageData?.packageId}` 
                                            : getHumanReadableDocId(confirmModal.record?.doc_id || '')
                                        }
                                    </div>
                                    <div className="flex flex-col gap-1.5 text-[11px] text-slate-500 dark:text-slate-400">
                                        <div>
                                            Obra / Proyecto: <strong className="text-indigo-600 dark:text-indigo-400 font-bold">
                                                {confirmModal.isPackage 
                                                    ? (confirmModal.packageData && confirmModal.packageData.records.length > 0 
                                                        ? getHumanReadableCollection(confirmModal.packageData.records[0].collection_name) 
                                                        : 'Varias Obras')
                                                    : getHumanReadableCollection(confirmModal.record?.collection_name || '')
                                                }
                                            </strong>
                                        </div>
                                        {confirmModal.isPackage && (
                                            <div>
                                                Detalle del Lote: <strong className="text-slate-700 dark:text-slate-200 font-bold">
                                                    {confirmModal.packageData?.records.length} elementos agrupados en esta hora
                                                </strong>
                                            </div>
                                        )}
                                        <div>
                                            Autor del cambio: <strong className="text-slate-700 dark:text-slate-200 font-bold">
                                                {confirmModal.isPackage 
                                                    ? "Varios autores en esta hora" 
                                                    : resolveBackupAuthor(confirmModal.record!)
                                                }
                                            </strong>
                                        </div>
                                        <div>
                                            Fecha de registro: <strong className="text-slate-700 dark:text-slate-200 font-bold">
                                                {confirmModal.isPackage 
                                                    ? new Date(confirmModal.packageData?.timestamp || '').toLocaleString('es-CL') 
                                                    : new Date(confirmModal.record?.changed_at || '').toLocaleString('es-CL')
                                                }
                                            </strong>
                                        </div>
                                    </div>
                                    {!confirmModal.isPackage && confirmModal.record && (
                                        <div className="mt-1 pt-2 border-t border-slate-200/50 dark:border-slate-800/50 text-xs">
                                            {renderPayloadDetails(confirmModal.record)}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Dynamic Contextual Dependency Analysis */}
                            {((!confirmModal.isPackage && (confirmModal.record?.doc_id === 'tasks' || confirmModal.record?.doc_id === 'levels' || confirmModal.record?.doc_id === 'units')) ||
                              (confirmModal.isPackage && confirmModal.packageData?.records.some(r => r.doc_id === 'tasks' || r.doc_id === 'levels' || r.doc_id === 'units'))) ? (
                                /* ESTRUCTURAL WARNING DIALOG */
                                <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900/30 p-4.5 rounded-2xl space-y-2 animate-in fade-in duration-300">
                                    <div className="flex items-center gap-2 text-red-600 dark:text-red-400 font-extrabold text-xs uppercase tracking-wider">
                                        <span className="material-symbols-outlined text-[18px]">warning</span>
                                        Cambio Estructural de Alto Impacto
                                    </div>
                                    <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
                                        ⚠️ <strong>Atención:</strong> Esta restauración <strong>SÍ alterará la estructura general del sistema</strong>. Vas a revertir a una versión previa de la Carta Gantt o la Estructura de Desglose de Trabajo (EDT).
                                    </p>
                                    <div className="pt-2 border-t border-red-200/40 dark:border-red-900/20 text-[11px]">
                                        <span className="font-extrabold text-red-700 dark:text-red-400 uppercase tracking-wide block mb-1">Módulos y dependencias afectadas:</span>
                                        <ul className="list-disc list-inside space-y-1 text-slate-600 dark:text-slate-400 font-semibold">
                                            <li>Matriz de Avance Físico (Asociación EDT)</li>
                                            <li>Planificación Semanal (Asignaciones operativas)</li>
                                            <li>Protocolos y Firmas de Calidad en Terreno</li>
                                            <li>Estados de Pago de Subcontratos vinculados</li>
                                        </ul>
                                    </div>
                                </div>
                            ) : (
                                /* OPERATIVO / TOTALMENTE AISLADO DIALOG */
                                <div className="bg-emerald-50 dark:bg-emerald-950/15 border border-emerald-200 dark:border-emerald-900/30 p-4.5 rounded-2xl space-y-1.5 animate-in fade-in duration-300">
                                    <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400 font-extrabold text-xs uppercase tracking-wider">
                                        <span className="material-symbols-outlined text-[18px]">gpp_good</span>
                                        Restauración Operativa Totalmente Aislada
                                    </div>
                                    <p className="text-xs text-slate-650 dark:text-emerald-300/80 leading-relaxed font-semibold">
                                        🛡️ <strong>Garantía de Aislamiento:</strong> La estructura general del sistema no será alterada con este cambio. El proceso revertirá únicamente datos operativos de esta obra en este módulo específico. Las demás obras, presupuestos y configuraciones globales del sistema permanecerán 100% protegidas e intactas.
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Action buttons */}
                        <div className="px-6 py-4.5 bg-slate-50 dark:bg-slate-900/50 border-t border-slate-200 dark:border-slate-800/80 flex justify-end gap-2 shrink-0">
                            <button
                                onClick={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
                                className="px-4 py-2.5 bg-white hover:bg-slate-100 text-slate-700 border border-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 dark:text-slate-300 dark:border-slate-700 rounded-xl font-bold text-xs transition-colors active:scale-95"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={confirmModal.onConfirm}
                                className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold text-xs transition-colors shadow-md shadow-indigo-600/10 active:scale-95 flex items-center gap-1.5"
                            >
                                <span className="material-symbols-outlined text-[16px]">settings_backup_restore</span>
                                Sí, Restaurar con Seguridad
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BackupsManager;
